import type {
  ClientFeature,
  ClientProduct,
  BuildPreview,
  CodeOrder,
  CodeOrderDetail,
  ImportResult,
  PalletStatus,
  SplitManyResult,
  SpreadResult,
  Access,
  AccessGroup,
  Client,
  DebtBalance,
  DebtEntry,
  DebtKindOption,
  Device,
  Contact,
  Conversation,
  InviteSent,
  MyPermissions,
  NamedRef,
  PermissionSection,
  PortalUserRow,
  Subscription,
  SubscriptionUsage,
  Tariff,
  Ticket,
  TicketCategory,
  TicketDetail,
  TicketKind,
  Kpi,
  Payout,
  PayoutPreview,
  PayoutStatus,
  Project,
  Rate,
  Source,
  Subclient,
  Summary,
  Task,
  TaskFilters,
  User,
  WorkStatus,
} from './types'

import { getServerUrl } from './lib/server'

const TOKEN_KEY = 'timetrack.token'

/** Адрес сервера. На десктопе задаётся пользователем и может меняться, поэтому
 *  спрашивается при каждом запросе, а не запоминается один раз при загрузке. */
export const apiBase = getServerUrl

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

/** Адреса входа: сессии на них ещё нет, что бы ни лежало в хранилище. */
const SIGN_IN_PATHS = [
  '/api/auth/login',
  '/api/auth/login-json',
  '/api/auth/code/request',
  '/api/auth/code/verify',
  '/api/devices/login',
]

const isSignIn = (path: string) =>
  SIGN_IN_PATHS.some((p) => path === p || path.startsWith(`${p}?`))

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = getToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  // FormData сам проставляет Content-Type с границей блоков — своё значение
  // сломало бы разбор файла на сервере.
  const isForm = init.body instanceof FormData
  if (init.body && !isForm && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(`${apiBase()}${path}`, { ...init, headers })

  // Сессия могла истечь только там, где она была. На самом входе её ещё нет,
  // и подменять ответ сервера нельзя: человек с опечаткой в пароле видел
  // «Сессия истекла, войдите заново» и перезагружал страницу по кругу, потому
  // что про пароль ему никто не сказал.
  if (res.status === 401 && token && !isSignIn(path)) {
    setToken(null)
    window.dispatchEvent(new CustomEvent('timetrack:unauthorized'))
    throw new ApiError(401, 'Сессия истекла, войдите заново')
  }
  if (!res.ok) {
    let detail = `Ошибка ${res.status}`
    try {
      const body = await res.json()
      if (typeof body.detail === 'string') detail = body.detail
      else if (Array.isArray(body.detail)) detail = body.detail.map((d: any) => d.msg).join('; ')
    } catch {
      /* тело не JSON — оставляем код */
    }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

function qs(params: Record<string, unknown> | TaskFilters): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) value.forEach((v) => sp.append(key, String(v)))
    else sp.append(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const api = {
  // --- auth ---
  login: (email: string, password: string) =>
    request<{ access_token: string; user: User }>('/api/auth/login-json', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () => request<User>('/api/auth/me'),

  // --- вход по одноразовому коду ---
  codeChannels: () => request<{ email: boolean; sms: boolean }>('/api/auth/code/channels'),
  requestCode: (destination: string) =>
    request<{ channel: string; sent: boolean }>('/api/auth/code/request', {
      method: 'POST',
      body: JSON.stringify({ destination }),
    }),
  verifyCode: (destination: string, code: string) =>
    request<{ access_token: string; user: User }>('/api/auth/code/verify', {
      method: 'POST',
      body: JSON.stringify({ destination, code }),
    }),
  // --- вход по коду устройства ---
  devices: () => request<Device[]>('/api/devices'),
  setPin: (device_uid: string, pin: string, label?: string) =>
    request<Device>('/api/devices/pin', {
      method: 'POST',
      body: JSON.stringify({ device_uid, pin, label: label ?? null }),
    }),
  loginByPin: (device_uid: string, pin: string) =>
    request<{ access_token: string }>('/api/devices/login', {
      method: 'POST',
      body: JSON.stringify({ device_uid, pin }),
    }),
  revokeDevice: (id: number) => request<void>(`/api/devices/${id}`, { method: 'DELETE' }),

  // --- тарифы и подписки ---
  tariffs: (includeInactive = false) =>
    request<Tariff[]>(`/api/tariffs${qs({ include_inactive: includeInactive || undefined })}`),
  createTariff: (body: Record<string, unknown>) =>
    request<Tariff>('/api/tariffs', { method: 'POST', body: JSON.stringify(body) }),
  updateTariff: (id: number, body: Record<string, unknown>) =>
    request<Tariff>(`/api/tariffs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTariff: (id: number) => request<void>(`/api/tariffs/${id}`, { method: 'DELETE' }),

  subscriptions: (clientId: number) =>
    request<Subscription[]>(`/api/clients/${clientId}/subscriptions`),
  subscriptionUsage: (clientId: number) =>
    request<SubscriptionUsage>(`/api/clients/${clientId}/subscriptions/usage`),
  createSubscription: (clientId: number, body: Record<string, unknown>) =>
    request<Subscription>(`/api/clients/${clientId}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateSubscription: (id: number, body: Record<string, unknown>) =>
    request<Subscription>(`/api/subscriptions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteSubscription: (id: number) =>
    request<void>(`/api/subscriptions/${id}`, { method: 'DELETE' }),

  // --- обращения ---
  tickets: (
    params: {
      client_id?: number
      assignee_id?: number
      open_only?: boolean
      category?: TicketCategory
      kind?: TicketKind
    } = {},
  ) =>
    request<Ticket[]>(`/api/tickets${qs(params)}`),
  ticket: (id: number) => request<TicketDetail>(`/api/tickets/${id}`),
  createTicket: (body: Record<string, unknown>) =>
    request<TicketDetail>('/api/tickets', { method: 'POST', body: JSON.stringify(body) }),
  updateTicket: (id: number, body: Record<string, unknown>) =>
    request<TicketDetail>(`/api/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  replyTicket: (id: number, body: string) =>
    request<TicketDetail>(`/api/tickets/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  noteTicket: (id: number, body: string) =>
    request<TicketDetail>(`/api/tickets/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  linkTicketTask: (ticketId: number, taskId: number) =>
    request<TicketDetail>(`/api/tickets/${ticketId}/tasks/${taskId}`, { method: 'POST' }),
  unlinkTicketTask: (ticketId: number, taskId: number) =>
    request<TicketDetail>(`/api/tickets/${ticketId}/tasks/${taskId}`, { method: 'DELETE' }),
  attachToTicket: (id: number, file: File, isResult = false) => {
    const form = new FormData()
    form.append('file', file)
    if (isResult) form.append('is_result', 'true')
    return request<TicketDetail>(`/api/tickets/${id}/files`, { method: 'POST', body: form })
  },

  markTicketFileResult: (ticketId: number, fileId: number, isResult: boolean) =>
    request<TicketDetail>(
      `/api/tickets/${ticketId}/files/${fileId}?is_result=${isResult}`,
      { method: 'PATCH' },
    ),
  /** Вложение обращения. Качаем через fetch, чтобы ушёл заголовок авторизации. */
  summary: () => request<Summary>('/api/summary'),

  // --- мессенджер ---
  conversations: () => request<Conversation[]>('/api/conversations'),
  conversation: (id: number) => request<Conversation>(`/api/conversations/${id}`),
  unreadChat: () => request<{ unread: number }>('/api/conversations/unread'),
  sendChat: (
    id: number,
    body: string,
    file?: File | null,
    opts?: { replyTo?: number | null; forwardOf?: number | null },
  ) => {
    const form = new FormData()
    form.append('body', body)
    if (file) form.append('file', file)
    if (opts?.replyTo) form.append('reply_to', String(opts.replyTo))
    if (opts?.forwardOf) form.append('forward_of', String(opts.forwardOf))
    return request<Conversation>(`/api/conversations/${id}/messages`, {
      method: 'POST',
      body: form,
    })
  },

  editChatMessage: (conversationId: number, messageId: number, body: string) =>
    request<Conversation>(`/api/conversations/${conversationId}/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),

  approveTicket: (ticketId: number, body: Record<string, unknown>) =>
    request<TicketDetail>(`/api/tickets/${ticketId}/approval`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  handToSpecialist: (ticketId: number, body: Record<string, unknown>) =>
    request<TicketDetail>(`/api/tickets/${ticketId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  downloadChatFile: (conversationId: number, messageId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/conversations/${conversationId}/files/${messageId}`,
      name,
      getToken(),
    ),

  downloadTicketFile: (ticketId: number, fileId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/tickets/${ticketId}/files/${fileId}`,
      name,
      getToken(),
    ),

  // --- заказы кодов маркировки ---
  codeOrders: (clientId?: number) =>
    request<CodeOrder[]>(`/api/code-orders${clientId ? `?client_id=${clientId}` : ''}`),
  codeOrder: (id: number) => request<CodeOrderDetail>(`/api/code-orders/${id}`),
  createCodeOrder: (body: {
    client_id: number
    title: string
    supply_date?: string
    note?: string | null
  }) =>
    request<CodeOrderDetail>('/api/code-orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteCodeOrder: (id: number) =>
    request<void>(`/api/code-orders/${id}`, { method: 'DELETE' }),
  updateCodeOrder: (id: number, body: Record<string, unknown>) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  uploadCodeOrderSource: (id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<CodeOrderDetail>(`/api/code-orders/${id}/source`, {
      method: 'POST',
      body: form,
    })
  },
  confirmCodeOrder: (id: number) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/confirm`, { method: 'POST' }),
  reopenCodeOrder: (id: number) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/reopen`, { method: 'POST' }),
  /** Пересчитать собранную руками поставку, ничего не записывая */
  previewCodeOrderBuild: (id: number, body: unknown) =>
    request<BuildPreview>(`/api/code-orders/${id}/build/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  buildCodeOrder: (id: number, body: unknown) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/build`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  codeOrderProducts: (id: number) =>
    request<ClientProduct[]>(`/api/code-orders/${id}/products`),
  uploadCodeOrderCatalog: (id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<ImportResult>(`/api/code-orders/${id}/catalog`, {
      method: 'POST',
      body: form,
    })
  },
  matchCodeOrderProduct: (id: number, itemId: number, productId: number) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/match`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, product_id: productId }),
    }),
  uploadCodes: (id: number, itemId: number, files: File[]) => {
    const form = new FormData()
    // СУЗ отдаёт заказ частями — берём все файлы разом
    files.forEach((f) => form.append('files', f))
    return request<CodeOrderDetail>(`/api/code-orders/${id}/items/${itemId}/codes`, {
      method: 'POST',
      body: form,
    })
  },
  splitItem: (id: number, itemId: number) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/items/${itemId}/split`, {
      method: 'POST',
    }),
  uploadGtd: (
    id: number,
    file: File,
    where: { truckId?: number; palletId?: number },
    реквизиты: { number?: string; issuedOn?: string } = {},
  ) => {
    const form = new FormData()
    form.append('file', file)
    if (where.truckId) form.append('truck_id', String(where.truckId))
    if (where.palletId) form.append('pallet_id', String(where.palletId))
    if (реквизиты.number) form.append('number', реквизиты.number)
    if (реквизиты.issuedOn) form.append('issued_on', реквизиты.issuedOn)
    return request<CodeOrderDetail>(`/api/code-orders/${id}/gtd`, {
      method: 'POST',
      body: form,
    })
  },
  spreadCodes: (id: number, files: File[], palletIds?: number[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    // Выделение сужает раздачу: выделили грузовик — коды лягут в него
    if (palletIds?.length) form.append('pallet_ids', palletIds.join(','))
    return request<SpreadResult>(`/api/code-orders/${id}/codes/spread`, {
      method: 'POST',
      body: form,
    })
  },
  splitMany: (id: number, itemIds: number[]) =>
    request<SplitManyResult>(`/api/code-orders/${id}/split`, {
      method: 'POST',
      body: JSON.stringify({ item_ids: itemIds }),
    }),
  setPalletStatus: (id: number, palletIds: number[], status: PalletStatus) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/pallets/status`, {
      method: 'POST',
      body: JSON.stringify({ pallet_ids: palletIds, status }),
    }),
  downloadBundle: (
    id: number,
    name: string,
    palletIds?: number[],
    what: 'all' | 'csv' | 'xml' = 'all',
  ) => {
    const q = new URLSearchParams()
    if (palletIds?.length) q.set('pallet_ids', palletIds.join(','))
    if (what !== 'all') q.set('what', what)
    const хвост = q.toString()
    return downloadFile(
      `${apiBase()}/api/code-orders/${id}/bundle${хвост ? `?${хвост}` : ''}`,
      name,
      getToken(),
    )
  },
  setLastSscc: (id: number, value: string) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/sscc/last`, {
      method: 'POST',
      body: JSON.stringify({ last_sscc: value }),
    }),
  downloadCodeOrderReport: (
    id: number,
    kind: 'pallets' | 'codes',
    name: string,
    palletIds?: number[],
  ) =>
    downloadFile(
      `${apiBase()}/api/code-orders/${id}/report/${kind}${
        palletIds?.length ? `?pallet_ids=${palletIds.join(',')}` : ''
      }`,
      name,
      getToken(),
    ),
  downloadCzOrder: (id: number, truckId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/code-orders/${id}/trucks/${truckId}/cz-order`,
      name,
      getToken(),
    ),
  assignSscc: (id: number, start: string) =>
    request<CodeOrderDetail>(`/api/code-orders/${id}/sscc`, {
      method: 'POST',
      body: JSON.stringify({ start }),
    }),
  downloadCodeOrderFile: (orderId: number, fileId: number, name: string) =>
    downloadFile(
      `${apiBase()}/api/code-orders/${orderId}/files/${fileId}`,
      name,
      getToken(),
    ),

  // --- кабинет клиента (со стороны менеджера) ---
  portalUsers: (clientId: number) =>
    request<PortalUserRow[]>(`/api/clients/${clientId}/portal`),
  invitePortalAdmin: (clientId: number, body: Record<string, unknown>) =>
    request<InviteSent>(`/api/clients/${clientId}/portal/admin`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  setPortalSeats: (clientId: number, portal_seats: number) =>
    request<{ portal_seats: number }>(`/api/clients/${clientId}/portal/seats`, {
      method: 'PATCH',
      body: JSON.stringify({ portal_seats }),
    }),
  setPortalUserActive: (clientId: number, id: number, isActive: boolean) =>
    request<PortalUserRow>(
      `/api/clients/${clientId}/portal/${id}?is_active=${isActive}`,
      { method: 'PATCH' },
    ),

  myPermissions: () => request<MyPermissions>('/api/me/permissions'),

  // --- группы доступа ---
  permissionCatalog: () => request<PermissionSection[]>('/api/permissions'),
  groups: () => request<AccessGroup[]>('/api/groups'),
  createGroup: (body: Record<string, unknown>) =>
    request<AccessGroup>('/api/groups', { method: 'POST', body: JSON.stringify(body) }),
  updateGroup: (id: number, body: Record<string, unknown>) =>
    request<AccessGroup>(`/api/groups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteGroup: (id: number) => request<void>(`/api/groups/${id}`, { method: 'DELETE' }),

  // --- пользователи ---
  users: (includeInactive = false) =>
    request<User[]>(`/api/users${qs({ include_inactive: includeInactive })}`),
  createUser: (body: Partial<User> & { password: string }) =>
    request<User>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: number, body: Record<string, unknown>) =>
    request<User>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deactivateUser: (id: number) => request<void>(`/api/users/${id}`, { method: 'DELETE' }),

  // --- справочники ---
  sources: (includeInactive = false) =>
    request<Source[]>(`/api/sources${qs({ include_inactive: includeInactive })}`),
  createSource: (body: Record<string, unknown>) =>
    request<Source>('/api/sources', { method: 'POST', body: JSON.stringify(body) }),
  updateSource: (id: number, body: Record<string, unknown>) =>
    request<Source>(`/api/sources/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSource: (id: number) => request<void>(`/api/sources/${id}`, { method: 'DELETE' }),

  clients: (params: { q?: string; source_id?: number; parent_id?: number; include_inactive?: boolean } = {}) =>
    request<Client[]>(`/api/clients${qs(params)}`),
  createClient: (body: Record<string, unknown>) =>
    request<Client>('/api/clients', { method: 'POST', body: JSON.stringify(body) }),
  clientFeatures: () => request<ClientFeature[]>('/api/client-features'),

  updateClient: (id: number, body: Record<string, unknown>) =>
    request<Client>(`/api/clients/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteClient: (id: number) => request<void>(`/api/clients/${id}`, { method: 'DELETE' }),

  subclients: (clientId?: number) =>
    request<Subclient[]>(`/api/subclients${qs({ client_id: clientId })}`),
  createSubclient: (body: Record<string, unknown>) =>
    request<Subclient>('/api/subclients', { method: 'POST', body: JSON.stringify(body) }),
  updateSubclient: (id: number, body: Record<string, unknown>) =>
    request<Subclient>(`/api/subclients/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSubclient: (id: number) => request<void>(`/api/subclients/${id}`, { method: 'DELETE' }),

  projects: (params: { client_id?: number; subclient_id?: number } = {}) =>
    request<Project[]>(`/api/projects${qs(params)}`),
  createProject: (body: Record<string, unknown>) =>
    request<Project>('/api/projects', { method: 'POST', body: JSON.stringify(body) }),
  updateProject: (id: number, body: Record<string, unknown>) =>
    request<Project>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteProject: (id: number) => request<void>(`/api/projects/${id}`, { method: 'DELETE' }),

  // --- задачи ---
  tasks: (filters: TaskFilters = {}) => request<Task[]>(`/api/tasks${qs(filters)}`),
  task: (id: number) => request<Task>(`/api/tasks/${id}`),
  runningTask: () => request<Task | null>('/api/tasks/running'),
  createTask: (body: Record<string, unknown>) =>
    request<Task>('/api/tasks', { method: 'POST', body: JSON.stringify(body) }),
  /** confirmLocked — согласие править задачу, уже попавшую в ведомость. */
  updateTask: (id: number, body: Record<string, unknown>, confirmLocked = false) =>
    request<Task>(`/api/tasks/${id}${qs({ confirm_locked: confirmLocked || undefined })}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTask: (id: number) => request<void>(`/api/tasks/${id}`, { method: 'DELETE' }),
  setEstimate: (
    id: number,
    lines: { title: string; hours: number }[],
    comment?: string,
    confirmLocked = false,
  ) =>
    request<Task>(`/api/tasks/${id}/estimate${qs({ confirm_locked: confirmLocked || undefined })}`, {
      method: 'PUT',
      body: JSON.stringify({ lines, comment: comment || null }),
    }),
  changeStatus: (id: number, work_status: WorkStatus, confirmLocked = false) =>
    request<Task>(`/api/tasks/${id}/status${qs({ confirm_locked: confirmLocked || undefined })}`, {
      method: 'POST',
      body: JSON.stringify({ work_status }),
    }),
  moveTask: (id: number, work_status: WorkStatus, board_order: number, confirmLocked = false) =>
    request<Task>(`/api/tasks/${id}/move${qs({ confirm_locked: confirmLocked || undefined })}`, {
      method: 'POST',
      body: JSON.stringify({ work_status, board_order }),
    }),

  startTimer: (id: number, clientUid?: string) =>
    request<Task>(`/api/tasks/${id}/start${qs({ client_uid: clientUid })}`, { method: 'POST' }),
  stopTimer: (id: number, finish = true) =>
    request<Task>(`/api/tasks/${id}/stop${qs({ finish })}`, { method: 'POST' }),

  addEntry: (taskId: number, body: Record<string, unknown>, confirmLocked = false) =>
    request<Task>(`/api/tasks/${taskId}/entries${qs({ confirm_locked: confirmLocked || undefined })}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateEntry: (
    taskId: number,
    entryId: number,
    body: Record<string, unknown>,
    confirmLocked = false,
  ) =>
    request<Task>(
      `/api/tasks/${taskId}/entries/${entryId}${qs({ confirm_locked: confirmLocked || undefined })}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteEntry: (taskId: number, entryId: number, confirmLocked = false) =>
    request<Task>(
      `/api/tasks/${taskId}/entries/${entryId}${qs({ confirm_locked: confirmLocked || undefined })}`,
      { method: 'DELETE' },
    ),

  // --- контакты клиента ---
  contacts: (clientId: number) => request<Contact[]>(`/api/clients/${clientId}/contacts`),
  createContact: (clientId: number, body: Record<string, unknown>) =>
    request<Contact>(`/api/clients/${clientId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateContact: (id: number, body: Record<string, unknown>) =>
    request<Contact>(`/api/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteContact: (id: number) => request<void>(`/api/contacts/${id}`, { method: 'DELETE' }),

  // --- справочники доступов ---
  products: (includeInactive = false) =>
    request<NamedRef[]>(`/api/products${qs({ include_inactive: includeInactive })}`),
  createProduct: (name: string) =>
    request<NamedRef>('/api/products', { method: 'POST', body: JSON.stringify({ name }) }),
  updateProduct: (id: number, body: Record<string, unknown>) =>
    request<NamedRef>(`/api/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteProduct: (id: number) => request<void>(`/api/products/${id}`, { method: 'DELETE' }),

  portalServices: (includeInactive = false) =>
    request<NamedRef[]>(`/api/portal-services${qs({ include_inactive: includeInactive })}`),
  createPortalService: (name: string) =>
    request<NamedRef>('/api/portal-services', { method: 'POST', body: JSON.stringify({ name }) }),
  updatePortalService: (id: number, body: Record<string, unknown>) =>
    request<NamedRef>(`/api/portal-services/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePortalService: (id: number) =>
    request<void>(`/api/portal-services/${id}`, { method: 'DELETE' }),

  // --- доступы ---
  accesses: (clientId: number) => request<Access[]>(`/api/clients/${clientId}/accesses`),
  /** Пароли приходят только отсюда — в списке их нет. */
  access: (id: number) => request<Access>(`/api/accesses/${id}`),
  createAccess: (clientId: number, body: Record<string, unknown>) =>
    request<Access>(`/api/clients/${clientId}/accesses`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateAccess: (id: number, body: Record<string, unknown>) =>
    request<Access>(`/api/accesses/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteAccess: (id: number) => request<void>(`/api/accesses/${id}`, { method: 'DELETE' }),

  async uploadAccessFile(accessId: number, file: File): Promise<Access> {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(`${apiBase()}/api/accesses/${accessId}/files`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
      body: form, // Content-Type проставит браузер вместе с boundary
    })
    if (!res.ok) {
      let detail = 'Не удалось загрузить файл'
      try {
        detail = (await res.json()).detail ?? detail
      } catch {
        /* тело не JSON */
      }
      throw new ApiError(res.status, detail)
    }
    return res.json()
  },

  deleteAccessFile: (id: number) => request<void>(`/api/access-files/${id}`, { method: 'DELETE' }),

  async downloadAccessFile(id: number, name: string): Promise<void> {
    const res = await fetch(`${apiBase()}/api/access-files/${id}`, {
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Не удалось скачать файл')
    const url = URL.createObjectURL(await res.blob())
    const a = document.createElement('a')
    a.href = url
    a.download = name
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },

  // --- KPI ---
  kpi: (filters: TaskFilters = {}) => request<Kpi>(`/api/kpi${qs(filters)}`),

  // --- ставки и выплаты ---
  rates: (userId?: number) => request<Rate[]>(`/api/rates${qs({ user_id: userId })}`),
  createRate: (body: Record<string, unknown>) =>
    request<Rate>('/api/rates', { method: 'POST', body: JSON.stringify(body) }),
  updateRate: (id: number, body: Record<string, unknown>) =>
    request<Rate>(`/api/rates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteRate: (id: number) => request<void>(`/api/rates/${id}`, { method: 'DELETE' }),

  payouts: (filters?: {
    user_id?: number
    date_from?: string
    date_to?: string
    payout_status?: string
  }) => request<Payout[]>(`/api/payouts${qs({ ...filters })}`),
  payout: (id: number) => request<Payout>(`/api/payouts/${id}`),
  payoutPreview: (
    user_id: number,
    period_start: string,
    period_end: string,
    filters: { client_id?: number; source_id?: number; project_id?: number } = {},
  ) =>
    request<PayoutPreview>(
      `/api/payouts/preview${qs({ user_id, period_start, period_end, ...filters })}`,
    ),

  // --- взаиморасчёты ---
  debtKinds: () => request<DebtKindOption[]>('/api/debts/kinds'),
  debtBalances: () => request<DebtBalance[]>('/api/debts/balances'),
  debts: (userId?: number) => request<DebtEntry[]>(`/api/debts${qs({ user_id: userId })}`),
  createDebt: (body: Record<string, unknown>) =>
    request<DebtEntry>('/api/debts', { method: 'POST', body: JSON.stringify(body) }),
  updateDebt: (id: number, body: Record<string, unknown>) =>
    request<DebtEntry>(`/api/debts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteDebt: (id: number) => request<void>(`/api/debts/${id}`, { method: 'DELETE' }),
  createPayout: (body: Record<string, unknown>) =>
    request<Payout>('/api/payouts', { method: 'POST', body: JSON.stringify(body) }),
  setPayoutStatus: (id: number, status: PayoutStatus) =>
    request<Payout>(`/api/payouts/${id}/status${qs({ new_status: status })}`, { method: 'POST' }),
  deletePayout: (id: number) => request<void>(`/api/payouts/${id}`, { method: 'DELETE' }),

  /** Выгрузка журнала — качаем через fetch, чтобы ушёл заголовок авторизации. */
  /** Смета одной задачи файлом Excel. Строки берутся с экрана, а не из базы:
   *  смету выгружают и до сохранения, пока её обсуждают с клиентом. */
  async exportEstimate(
    taskId: number,
    lines: { title: string; hours: number }[],
    fileName: string,
  ): Promise<void> {
    const res = await fetch(`${apiBase()}/api/reports/estimate.xlsx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken() ?? ''}`,
      },
      body: JSON.stringify({ task_id: taskId, lines }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Не удалось сформировать выгрузку')
    saveBlob(await res.blob(), fileName)
  },

  async exportJournal(filters: TaskFilters = {}): Promise<void> {
    const res = await fetch(`${apiBase()}/api/reports/journal.xlsx${qs(filters)}`, {
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Не удалось сформировать выгрузку')
    saveBlob(await res.blob(), `Журнал_${new Date().toISOString().slice(0, 10)}.xlsx`)
  },
}

/**
 * Имя файла из заголовка Content-Disposition.
 *
 * Кириллица едет в нём в процентах (filename*=utf-8''...), и без разбора
 * пользователь получил бы файл со случайным именем. Если заголовка нет —
 * берём запасное имя, которое знает вызывающий.
 */
export function fileNameFrom(res: Response, fallback: string): string {
  const header = res.headers.get('content-disposition') ?? ''
  const encoded = /filename\*=utf-8''([^;]+)/i.exec(header)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      /* битая кодировка — уходим на запасное имя */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1] : fallback
}

/**
 * Скачать файл с сервера и отдать пользователю.
 *
 * Именно так, а не ссылкой <a download>: ссылка не несёт заголовок
 * авторизации, и сервер отвечает на неё отказом. Браузер послушно сохраняет
 * этот отказ под именем файла — человек получает вместо таблицы строчку
 * JSON и не понимает, что произошло.
 */
export async function downloadFile(
  url: string,
  fallbackName: string,
  token: string | null,
): Promise<void> {
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    let detail = `Ошибка ${res.status}`
    try {
      const body = await res.json()
      if (typeof body.detail === 'string') detail = body.detail
    } catch {
      /* тело не JSON — оставляем код */
    }
    throw new ApiError(res.status, detail)
  }
  saveBlob(await res.blob(), fileNameFrom(res, fallbackName))
}

/** Отдать файл пользователю. Ссылка создаётся и удаляется на лету: постоянный
 *  элемент в разметке держал бы объект в памяти до перезагрузки страницы. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
